// @/utils/dateUtils.js
/**
 * Date utilities for consistent formatting and timezone handling
 *
 * Provides comprehensive date manipulation functions with special handling
 * for Snowflake date formats and timezone corrections.
 *
 * Features:
 * - Snowflake format detection and correction
 * - Timezone normalization for consistent display
 * - Locale-aware date formatting with i18n support
 * - Request status detection with date comparison
 */

/**
 * Normalize locale code to browser-compatible format
 * Maps application locale codes to proper BCP 47 language tags
 *
 * @param {string} locale - Application locale code
 * @returns {string} Browser-compatible locale string
 */
function normalizeLocaleForDate(locale) {
  if (!locale) return 'en-US';

  const lower = locale.toLowerCase();

  // Map app locales to BCP 47 tags
  const localeMap = {
    'en': 'en-US',
    'de': 'de-DE',
    'es': 'es-ES',
    'fr': 'fr-FR',
    'it': 'it-IT',
    'pt': 'pt-PT',
    'zh': 'zh-CN',
    'zh-cn': 'zh-CN',
    'zh-tw': 'zh-TW'
  };

  return localeMap[lower] || locale;
}

/**
 * Detect if a date string is in Snowflake format (without Z timezone)
 * Identifies date strings in Snowflake's specific output format
 * 
 * @param {string} dateString - Date string to check
 * @returns {boolean} True if date is in Snowflake format
 */
export function isSnowflakeFormat(dateString) {
  if (!dateString) return false;
  
  // Snowflake format: 2025-03-29 11:14:28.616 (has space, no Z, no T)
  return typeof dateString === 'string' && 
         dateString.includes(' ') && 
         !dateString.includes('Z') && 
         !dateString.includes('T');
}

/**
 * Check if a date string has already been timezone-fixed
 * Prevents duplicate corrections to already processed dates
 * 
 * @param {string} dateString - Date string to check
 * @returns {boolean} True if date has already been fixed
 */
export function isAlreadyCorrected(dateString) {
  if (!dateString) return false;
  
  // Dates that have already been processed by normalizeSnowflakeDate have ISO format
  return typeof dateString === 'string' && 
         dateString.includes('T') && 
         dateString.includes('Z');
}

/**
 * Normalize Snowflake date format to ISO 8601
 * Converts "2025-03-29 11:14:28.616" to "2025-03-29T11:14:28.616Z"
 * Snowflake timestamps are UTC — just reformat, no offset needed.
 *
 * @param {string} dateString - Snowflake date string
 * @returns {string} ISO date string
 */
export function fixSnowflakeTimezone(dateString) {
  if (!dateString) return dateString;

  // If already in ISO format with timezone, return as is
  if (isAlreadyCorrected(dateString)) return dateString;

  // If not in Snowflake format, return as is
  if (!isSnowflakeFormat(dateString)) return dateString;

  try {
    // Convert to ISO — Snowflake dates are UTC, no offset needed
    const isoString = dateString.replace(' ', 'T') + 'Z';
    const date = new Date(isoString);
    return date.toISOString();
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error normalizing Snowflake date:', error);
    }
    return dateString;
  }
}

/**
 * Format a date for display in the UI with standard formatting
 * Ensures consistent date presentation throughout the application
 *
 * @param {string|Date} dateString - Date to format
 * @param {string} [locale='en'] - Locale code for formatting (e.g., 'en', 'de', 'zh-CN')
 * @returns {string} Formatted date string or "Never" if no date
 */
export function formatDisplayDate(dateString, locale = 'en') {
  if (!dateString) return 'Never';

  let date;
  if (typeof dateString === 'string') {
    // Handle Snowflake format dates before formatting
    if (isSnowflakeFormat(dateString) && !isAlreadyCorrected(dateString)) {
      dateString = fixSnowflakeTimezone(dateString);
    }
    date = new Date(dateString);
  } else if (dateString instanceof Date) {
    date = dateString;
  } else {
    return 'Never';
  }

  if (isNaN(date.getTime())) return 'Never';

  // Normalize locale for browser API
  const browserLocale = normalizeLocaleForDate(locale);

  // Use locale-specific formatting
  try {
    return date.toLocaleDateString(browserLocale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    // Fallback for older browsers or invalid locales
    try {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      const month = date.toLocaleString('en-US', { month: 'short' });
      return `${month} ${date.getDate()}, ${date.getFullYear()}`;
    }
  }
}

/**
 * Get the effective LastSubmitDate for a request, accounting for post-submission accepts.
 *
 * If a request has its own LastSubmitDate, that's authoritative.
 * If it doesn't, the global LastSubmitDate only applies if the request was accepted
 * BEFORE that submission. Requests accepted after the last submission haven't been
 * submitted yet — the global date must not suppress their badge.
 *
 * @param {Object} request - Customer request object
 * @param {string|null} globalLastSubmitDate - Global last submission date
 * @returns {string|null} The effective submit date for this request, or null
 */
export function getEffectiveSubmitDate(request, globalLastSubmitDate) {
  // Per-request date is always authoritative
  if (request.LastSubmitDate) return request.LastSubmitDate;

  if (!globalLastSubmitDate) return null;

  // Check if this request was accepted AFTER the last submission
  const acceptDate = request.LastLinkingStatusChange;
  if (acceptDate) {
    let normalizedAccept = acceptDate;
    let normalizedGlobal = globalLastSubmitDate;

    if (isSnowflakeFormat(acceptDate)) {
      normalizedAccept = fixSnowflakeTimezone(acceptDate);
    }
    if (isSnowflakeFormat(globalLastSubmitDate)) {
      normalizedGlobal = fixSnowflakeTimezone(globalLastSubmitDate);
    }

    // If accepted after the submission, this request wasn't part of it
    if (new Date(normalizedAccept) > new Date(normalizedGlobal)) {
      return null;
    }
  }

  return globalLastSubmitDate;
}

/**
 * Calculate whether a customer request needs attention based on its status and dates.
 * Single source of truth for the needsAttention badge logic.
 *
 * Rules:
 * - Declined → never needs attention
 * - Invited → always needs attention
 * - Accepted → needs attention if no submission covers it, or if reminder is newer than submission
 *
 * @param {Object} request - Customer request object (Status, LastLinkingRequest, LinkingCreated, etc.)
 * @param {string|null} lastSubmitDate - Global last submission date for fallback
 * @returns {boolean} Whether the request needs attention
 */
export function calculateNeedsAttention(request, lastSubmitDate) {
  if (!request) return false;

  const status = (request.Status || '').toLowerCase();

  if (status === 'declined') return false;
  if (status === 'invited') return true;

  if (status === 'accepted') {
    const effectiveSubmit = getEffectiveSubmitDate(request, lastSubmitDate);

    // No submission covers this request
    if (!effectiveSubmit) return true;

    const lastRequest = request.LastLinkingRequest || request.LinkingCreated;
    if (lastRequest) {
      const normalizedRequest = isSnowflakeFormat(lastRequest) ? fixSnowflakeTimezone(lastRequest) : lastRequest;
      const normalizedSubmit = isSnowflakeFormat(effectiveSubmit) ? fixSnowflakeTimezone(effectiveSubmit) : effectiveSubmit;
      return new Date(normalizedRequest) > new Date(normalizedSubmit);
    }
  }

  return false;
}

